/** Canvas caption strip rendering — shared by the upload pipeline and the live preview (WYSIWYG). */

import type { StripText } from "./caption";
import type { CaptionStyle } from "../types";

export interface StripMetrics {
  fontPx: number;
  smallFontPx: number;
  paddingPx: number;
  lineGapPx: number;
}

export function stripMetrics(imageWidth: number): StripMetrics {
  // Scale typography with image width; clamps keep tiny previews legible
  // and huge originals tasteful.
  const fontPx = Math.round(Math.min(Math.max(imageWidth * 0.034, 13), 72));
  return {
    fontPx,
    smallFontPx: Math.round(fontPx * 0.82),
    paddingPx: Math.round(fontPx * 0.9),
    lineGapPx: Math.round(fontPx * 0.35)
  };
}

const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * Wrap the caption into at most `maxLines` lines that fit `maxWidth`,
 * ellipsizing the last line if needed.
 */
export function wrapCaption(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number
): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";

  for (let i = 0; i < words.length; i++) {
    const candidate = current ? `${current} ${words[i]}` : words[i]!;
    if (ctx.measureText(candidate).width <= maxWidth || !current) {
      current = candidate;
      continue;
    }
    lines.push(current);
    current = words[i]!;
    if (lines.length === maxLines - 1) {
      // Last allowed line: consume the rest and ellipsize.
      let rest = words.slice(i).join(" ");
      while (rest.length > 1 && ctx.measureText(`${rest}…`).width > maxWidth) {
        rest = rest.slice(0, -1).trimEnd();
      }
      lines.push(`${rest}…`);
      return lines;
    }
  }
  if (current) lines.push(current);
  return lines;
}

/**
 * Draw a semi-opaque caption strip along the bottom of the canvas.
 * The same routine renders the final upload and the live preview.
 */
export function drawCaptionStrip(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strip: StripText
): void {
  const m = stripMetrics(width);
  const maxTextWidth = width - m.paddingPx * 2;

  ctx.save();
  ctx.textBaseline = "alphabetic";

  ctx.font = `600 ${m.fontPx}px ${FONT_STACK}`;
  const captionLines = wrapCaption(ctx, strip.captionLine, maxTextWidth, 2);

  const hasAttribution = strip.attribution.length > 0;
  const contentHeight =
    captionLines.length * (m.fontPx + m.lineGapPx) +
    (hasAttribution ? m.smallFontPx + m.lineGapPx : 0) -
    m.lineGapPx;
  const stripHeight = contentHeight + m.paddingPx * 2;
  const stripTop = height - stripHeight;

  // Soft gradient into a solid strip: reads cleanly over any photo.
  const fade = Math.round(m.fontPx * 1.2);
  const gradient = ctx.createLinearGradient(0, stripTop - fade, 0, stripTop);
  gradient.addColorStop(0, "rgba(20, 16, 12, 0)");
  gradient.addColorStop(1, "rgba(20, 16, 12, 0.62)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, stripTop - fade, width, fade);
  ctx.fillStyle = "rgba(20, 16, 12, 0.62)";
  ctx.fillRect(0, stripTop, width, stripHeight);

  let y = stripTop + m.paddingPx + m.fontPx * 0.8;
  ctx.fillStyle = "rgba(255, 253, 250, 0.98)";
  ctx.font = `600 ${m.fontPx}px ${FONT_STACK}`;
  for (const line of captionLines) {
    ctx.fillText(line, m.paddingPx, y, maxTextWidth);
    y += m.fontPx + m.lineGapPx;
  }

  if (hasAttribution) {
    ctx.font = `400 ${m.smallFontPx}px ${FONT_STACK}`;
    ctx.fillStyle = "rgba(255, 253, 250, 0.85)";
    y = y - (m.fontPx + m.lineGapPx) + m.smallFontPx + m.lineGapPx * 2;
    ctx.fillText(strip.attribution, m.paddingPx, y, maxTextWidth);
  }

  ctx.restore();
}

/* ------------------------------------------------------------ framesafe style */

/**
 * Title-safe insets, as a fraction of each axis, for the crop-resistant style.
 * Broadcast title-safe is ~10%; we use 12% so a frame that letterboxes/crops the
 * photo to its own aspect ratio (typically trimming ~10–15% off the edges) can't
 * clip the caption. Both the scrim card AND the text sit inside this zone.
 */
export const SAFE_MARGIN_X_RATIO = 0.12;
export const SAFE_MARGIN_Y_RATIO = 0.12;

/**
 * Overscan insets for the 'framefill' style. Smaller than framesafe's 12%
 * because framefill composes the output AT the frame's aspect ratio — the frame
 * displays the whole thing, so there is no Aura crop to survive. A little inset
 * (~8%) is just overscan insurance so the panel never kisses the very edge.
 */
export const FRAMEFILL_MARGIN_X_RATIO = 0.08;
export const FRAMEFILL_MARGIN_Y_RATIO = 0.08;

interface FramesafeLine {
  text: string;
  /** Alphabetic-baseline y in canvas pixels. */
  baseline: number;
  fontPx: number;
  weight: 400 | 600;
}

export interface FramesafeLayout {
  /** Rounded scrim card behind the text — fully inside the safe zone. */
  band: { x: number; y: number; width: number; height: number };
  radius: number;
  /** Left edge where text is drawn (band.x + inner padding). */
  textX: number;
  /** Max width passed to fillText so glyphs never spill past the card. */
  textMaxWidth: number;
  lines: FramesafeLine[];
  /** Resolved insets used, in pixels — handy for assertions/debugging. */
  safeMarginX: number;
  safeMarginY: number;
}

/**
 * Pure geometry for the framesafe caption: where the scrim card and each text
 * line land. Kept separate from drawing so it is unit-testable without a real
 * canvas (only ctx.measureText / ctx.font are used here). Everything it returns
 * is inset from every edge by at least the safe margin, so a cropping frame
 * can't cut it off.
 */
export function framesafeLayout(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strip: StripText,
  marginXRatio: number = SAFE_MARGIN_X_RATIO,
  marginYRatio: number = SAFE_MARGIN_Y_RATIO
): FramesafeLayout {
  const m = stripMetrics(width);
  const safeMarginX = Math.round(width * marginXRatio);
  const safeMarginY = Math.round(height * marginYRatio);

  const maxBandWidth = Math.max(1, width - safeMarginX * 2);
  const wrapWidth = Math.max(1, maxBandWidth - m.paddingPx * 2);

  ctx.font = `600 ${m.fontPx}px ${FONT_STACK}`;
  const captionLines = wrapCaption(ctx, strip.captionLine, wrapWidth, 2);
  const hasAttribution = strip.attribution.length > 0;
  const nBlocks = captionLines.length + (hasAttribution ? 1 : 0);

  // Natural (unscaled) card height, before any fit-to-canvas.
  const naturalContent =
    captionLines.length * m.fontPx +
    (hasAttribution ? m.smallFontPx : 0) +
    (nBlocks - 1) * m.lineGapPx;
  const naturalBandHeight = naturalContent + m.paddingPx * 2;

  // The card must live between the TOP and BOTTOM safe margins. On extreme
  // wide-short (panorama) canvases a tall caption would otherwise push the card
  // above the top margin or off-canvas (bug #99/#101). Uniformly shrink the whole
  // block to fit the available vertical band — text scales with the card, so it
  // never overflows. Normal portrait/landscape ratios keep scale = 1 (unchanged
  // output), because their natural card already fits.
  const availableBandHeight = Math.max(1, height - safeMarginY * 2);
  const scale =
    naturalBandHeight > availableBandHeight ? availableBandHeight / naturalBandHeight : 1;

  const fontPx = m.fontPx * scale;
  const smallFontPx = m.smallFontPx * scale;
  const innerPad = m.paddingPx * scale;
  const lineGap = m.lineGapPx * scale;
  const textMaxWidth = Math.max(1, maxBandWidth - innerPad * 2);

  // Line blocks (caption at 600, attribution at 400/smaller).
  const blocks: { text: string; fontPx: number; weight: 400 | 600 }[] = captionLines.map(
    (text) => ({ text, fontPx, weight: 600 as const })
  );
  if (hasAttribution) {
    blocks.push({ text: strip.attribution, fontPx: smallFontPx, weight: 400 });
  }

  // Widest actual line drives the (left-anchored) card width so it hugs the text.
  let widest = 0;
  for (const b of blocks) {
    ctx.font = `${b.weight} ${b.fontPx}px ${FONT_STACK}`;
    widest = Math.max(widest, ctx.measureText(b.text).width);
  }
  const contentWidth = Math.min(widest, textMaxWidth);

  // Vertical layout relative to the content top (baselines via ~0.8em ascent).
  const relBaselines: number[] = [];
  let cursor = 0;
  for (let i = 0; i < blocks.length; i++) {
    if (i > 0) cursor += lineGap;
    relBaselines.push(cursor + blocks[i]!.fontPx * 0.8);
    cursor += blocks[i]!.fontPx;
  }
  const contentHeight = cursor;

  // Kept in float space until the end so the vertical invariants hold exactly;
  // never let the card exceed the available band or breach either margin.
  const bandHeight = Math.min(contentHeight + innerPad * 2, availableBandHeight);
  const bandWidth = Math.round(Math.min(contentWidth + innerPad * 2, maxBandWidth));
  const bandX = safeMarginX;
  // Bottom-anchored for normal ratios; never above the top safe margin.
  const bandY = Math.max(safeMarginY, Math.round(height - safeMarginY - bandHeight));
  const bandHeightInt = Math.min(Math.round(bandHeight), height - safeMarginY - bandY);
  const textX = bandX + Math.round(innerPad);

  const lines: FramesafeLine[] = blocks.map((b, i) => ({
    text: b.text,
    fontPx: b.fontPx,
    weight: b.weight,
    baseline: Math.round(bandY + innerPad + relBaselines[i]!)
  }));

  return {
    band: { x: bandX, y: bandY, width: bandWidth, height: bandHeightInt },
    radius: Math.round(Math.min(fontPx * 0.7, bandHeightInt / 2)),
    textX,
    textMaxWidth: contentWidth,
    lines,
    safeMarginX,
    safeMarginY
  };
}

/** Trace a rounded-rectangle path (roundRect isn't in every canvas impl). */
function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

interface ScrimStyle {
  /** Fraction-of-axis insets for the rounded card (and its text). */
  marginXRatio: number;
  marginYRatio: number;
  /** Card fill (semi-opaque dark panel). */
  panelFill: string;
}

/**
 * Shared renderer for the bottom-left rounded scrim card used by BOTH the
 * framesafe and framefill styles. Geometry comes from `framesafeLayout` (with
 * the style's own margins); only the insets and panel colour differ.
 */
function drawScrimCaption(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strip: StripText,
  style: ScrimStyle
): void {
  const layout = framesafeLayout(ctx, width, height, strip, style.marginXRatio, style.marginYRatio);
  const { band, radius } = layout;

  ctx.save();
  ctx.textBaseline = "alphabetic";

  // Soft drop shadow lifts the card off busy photos; cleared before drawing text.
  ctx.shadowColor = "rgba(0, 0, 0, 0.35)";
  ctx.shadowBlur = Math.round(band.height * 0.12);
  ctx.shadowOffsetY = Math.round(band.height * 0.03);
  roundedRectPath(ctx, band.x, band.y, band.width, band.height, radius);
  ctx.fillStyle = style.panelFill;
  ctx.fill();

  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  for (const line of layout.lines) {
    ctx.font = `${line.weight} ${line.fontPx}px ${FONT_STACK}`;
    ctx.fillStyle =
      line.weight === 600 ? "rgba(255, 253, 250, 0.98)" : "rgba(255, 253, 250, 0.85)";
    ctx.fillText(line.text, layout.textX, line.baseline, layout.textMaxWidth);
  }

  ctx.restore();
}

/**
 * Crop-resistant caption: a clean rounded scrim card, inset from every edge by
 * the title-safe margin, with the same content as the classic strip. Readable
 * over any photo (opaque-enough card + soft shadow), and safe from frame crop.
 */
export function drawCaptionFramesafe(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strip: StripText
): void {
  drawScrimCaption(ctx, width, height, strip, {
    marginXRatio: SAFE_MARGIN_X_RATIO,
    marginYRatio: SAFE_MARGIN_Y_RATIO,
    panelFill: "rgba(20, 16, 12, 0.58)"
  });
}

/**
 * Framefill overlay (Option B look): the same bottom-left rounded scrim card,
 * inset by only the small framefill overscan margin. The caller has already
 * composed the canvas AT the frame's aspect ratio with the photo filling it, so
 * this just lays the caption panel over the bottom-left corner — nothing here
 * changes the canvas dimensions. Panel is a semi-opaque near-black for a clean,
 * legible overlay over the photo.
 */
export function drawCaptionFramefill(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strip: StripText
): void {
  drawScrimCaption(ctx, width, height, strip, {
    marginXRatio: FRAMEFILL_MARGIN_X_RATIO,
    marginYRatio: FRAMEFILL_MARGIN_Y_RATIO,
    panelFill: "rgba(10, 14, 16, 0.55)"
  });
}

/**
 * Render the caption in the property's configured style. The SAME dispatcher
 * drives the live preview and the final composite, so preview == upload.
 */
export function drawCaption(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  strip: StripText,
  style: CaptionStyle
): void {
  if (style === "classic") {
    drawCaptionStrip(ctx, width, height, strip);
  } else if (style === "framefill") {
    // NOTE: for 'framefill' the canvas is already composed at the frame aspect
    // (photo cover-cropped to fill it) by the pipeline/preview; this only
    // overlays the caption panel. See pipeline.ts / main.ts.
    drawCaptionFramefill(ctx, width, height, strip);
  } else {
    drawCaptionFramesafe(ctx, width, height, strip);
  }
}
